import { formatCurrency } from 'utils/formatCurrency';

export type StoreReconciliationTone = 'matched' | 'surplus' | 'deficit' | 'missing' | 'unavailable';

export interface StoreReconciliationStatus {
  label: string;
  detail?: string;
  tone: StoreReconciliationTone;
}

interface StoreReconciliationStatusInput {
  isAuditDataMissing: boolean;
  isPosDataMissing: boolean;
  isLottoDataMissing: boolean;
  isPosConnected: boolean;
  isLottoConnected: boolean;
  totalVariance: number;
}

const formatSignedCurrency = (value: number): string => {
  if (value > 0) return `+${formatCurrency(Math.abs(value))}`;
  if (value < 0) return `-${formatCurrency(Math.abs(value))}`;
  return formatCurrency(0);
};

export const buildStoreReconciliationStatus = ({
  isAuditDataMissing,
  isPosDataMissing,
  isLottoDataMissing,
  isPosConnected,
  isLottoConnected,
  totalVariance,
}: StoreReconciliationStatusInput): StoreReconciliationStatus => {
  const missingSources = [
    isAuditDataMissing ? 'AUDIT' : null,
    isPosConnected && isPosDataMissing ? 'POS' : null,
    isLottoConnected && isLottoDataMissing ? 'LOTTO' : null,
  ].filter((source): source is string => Boolean(source));

  if (missingSources.length > 0) {
    return {
      label: `MISSING ${missingSources.join(' + ')}`,
      tone: 'missing',
    };
  }

  const unavailableSources = [
    !isPosConnected ? 'POS NOT CONNECTED' : null,
    !isLottoConnected ? 'LOTTO NOT ENABLED' : null,
  ].filter((source): source is string => Boolean(source));

  if (unavailableSources.length > 0) {
    return {
      label: unavailableSources.join(' + '),
      tone: 'unavailable',
    };
  }

  const normalizedVariance = Number(totalVariance.toFixed(2));
  if (normalizedVariance > 0) {
    return {
      label: 'SURPLUS',
      detail: formatSignedCurrency(normalizedVariance),
      tone: 'surplus',
    };
  }

  if (normalizedVariance < 0) {
    return {
      label: 'DEFICIT',
      detail: formatSignedCurrency(normalizedVariance),
      tone: 'deficit',
    };
  }

  return { label: 'MATCHED', tone: 'matched' };
};

export const getStoreSourceValue = (
  isConnected: boolean,
  isMissing: boolean,
  unavailableLabel: string
): string => isConnected && isMissing ? '-' : unavailableLabel;

interface CanInvestigateScratcherInput {
  isSingleDay: boolean;
  isSuperAdmin: boolean;
  storeUserRole?: string;
  auditCount: number;
  isAuditDataMissing: boolean;
  isPosConnected: boolean;
  isPosDataMissing: boolean;
  quantityVariance?: number;
  revenueVariance?: number;
}

export const canInvestigateScratcher = ({
  isSingleDay,
  isSuperAdmin,
  storeUserRole,
  auditCount,
  isAuditDataMissing,
  isPosConnected,
  isPosDataMissing,
  quantityVariance,
  revenueVariance,
}: CanInvestigateScratcherInput): boolean => {
  const normalizedRole = String(storeUserRole || '').toLowerCase();
  const hasPermission = isSuperAdmin || normalizedRole === 'manager' || normalizedRole === 'owner';
  const hasQuantityMismatch = Number(quantityVariance || 0) !== 0;
  const hasRevenueMismatch = Math.round(Number(revenueVariance || 0) * 100) !== 0;

  return isSingleDay
    && hasPermission
    && auditCount > 0
    && !isAuditDataMissing
    && isPosConnected
    && !isPosDataMissing
    && (hasQuantityMismatch || hasRevenueMismatch);
};
